import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft, Check, Copy, Download, FileText, Leaf, Link as LinkIcon, Search,
  Trash2, UserCheck, Users, X,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { ShareButton } from "../components/ShareButton";
import { registrationInvite } from "../lib/shareText";

/**
 * Registration settings and the attendee list.
 *
 * These live together because they are one job: open the doors, watch who comes
 * through. Kept off EventDetailPage deliberately — that page is already the
 * largest in the app and this is a different task with a different audience.
 *
 * Registration is closed by default on the backend, so this screen is what
 * makes the whole registration flow usable at all.
 */

export default function AttendeesPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { authAxios, canEdit } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [stats, setStats] = useState(null);
  const [dietary, setDietary] = useState(null);
  const [shareToken, setShareToken] = useState("");
  const [query, setQuery] = useState("");
  const [form, setForm] = useState({
    registration_open: false,
    registration_capacity: "",
    registration_message: "",
    registration_allow_party: false,
  });

  const load = useCallback(async () => {
    try {
      const api = authAxios();
      const [list, s, d, ev] = await Promise.all([
        api.get(`/events/${eventId}/attendees`),
        api.get(`/events/${eventId}/check-in/stats`),
        api.get(`/events/${eventId}/dietary`),
        api.get(`/events/${eventId}`),
      ]);
      setAttendees(list.data);
      setStats(s.data);
      setDietary(d.data);
      setShareToken(ev.data.share_token || "");
      setForm({
        eventTitle: ev.data.title || "",
        registration_open: !!ev.data.registration_open,
        registration_capacity: ev.data.registration_capacity ?? "",
        registration_message: ev.data.registration_message ?? "",
        registration_allow_party: !!ev.data.registration_allow_party,
      });
    } catch {
      toast.error("Could not load registrations");
    } finally {
      setLoading(false);
    }
  }, [authAxios, eventId]);

  useEffect(() => { load(); }, [load]);

  const save = async (overrides = {}) => {
    setSaving(true);
    const next = { ...form, ...overrides };
    try {
      const api = authAxios();
      await api.put(`/events/${eventId}/registration`, {
        registration_open: next.registration_open,
        registration_capacity: next.registration_capacity === ""
          ? null : Number(next.registration_capacity),
        registration_message: next.registration_message,
        registration_allow_party: !!next.registration_allow_party,
      });
      setForm(next);
      toast.success("Registration settings saved");
      load();
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const openRegistration = async () => {
    // Opening registration needs a share link to send people to; make sure one
    // exists rather than leaving the organiser with a setting and no URL.
    if (!shareToken) {
      try {
        const api = authAxios();
        const res = await api.post(`/events/${eventId}/share`);
        setShareToken(res.data.share_token);
      } catch {
        toast.error("Could not create the registration link");
        return;
      }
    }
    save({ registration_open: true });
  };

  const removeAttendee = async (attendee) => {
    if (!window.confirm(`Remove ${attendee.name} from this event?`)) return;
    try {
      const api = authAxios();
      await api.delete(`/attendees/${attendee.attendee_id}`);
      toast.success(`${attendee.name} removed`);
      load();
    } catch {
      toast.error("Could not remove attendee");
    }
  };

  const registrationUrl = shareToken
    ? `${window.location.origin}/register/${shareToken}`
    : "";

  const copyLink = () => {
    navigator.clipboard?.writeText(registrationUrl);
    toast.success("Registration link copied");
  };

  // Browsers cannot attach an Authorization header to a download, so the
  // export endpoints accept the token as a query parameter (same as iCal).
  const downloadList = (format) => {
    const api = authAxios();
    const base = api.defaults?.baseURL || `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
    const token = localStorage.getItem("auth_token");
    window.open(`${base}/events/${eventId}/attendees/export.${format}?token=${token}`, "_blank");
  };

  const filtered = attendees.filter((a) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [a.name, a.email, a.code].some((v) => (v || "").toLowerCase().includes(q));
  });

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-28 bg-muted rounded-xl animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="-ml-2 gap-1.5"
                onClick={() => navigate(`/events/${eventId}`)} data-testid="attendees-back">
          <ArrowLeft className="h-4 w-4" />
          Event
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5"
                onClick={() => navigate(`/events/${eventId}/scan`)} data-testid="attendees-scan">
          <UserCheck className="h-4 w-4" />
          Check-in
        </Button>
      </div>

      <h1 className="text-xl font-bold text-foreground">Registrations</h1>

      {/* Registration control. Without this, nothing else on this screen happens. */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-foreground">
                {form.registration_open ? "Registration is open" : "Registration is closed"}
              </p>
              <p className="text-sm text-muted-foreground">
                {form.registration_open
                  ? "Anyone with the link below can sign up."
                  : "Nobody can sign up until you open it."}
              </p>
            </div>
            {canEdit && (
              form.registration_open ? (
                <Button variant="outline" className="h-11 gap-1.5" disabled={saving}
                        onClick={() => save({ registration_open: false })}
                        data-testid="registration-close">
                  <X className="h-4 w-4" />Close
                </Button>
              ) : (
                <Button className="h-11 gap-1.5" disabled={saving} onClick={openRegistration}
                        data-testid="registration-open">
                  <Check className="h-4 w-4" aria-hidden="true" />Open
                </Button>
              )
            )}
          </div>

          {form.registration_open && registrationUrl && (
            <div className="flex flex-col sm:flex-row gap-4 items-center bg-muted rounded-lg p-4">
              <div className="bg-white p-2 rounded" data-testid="registration-qr">
                <QRCodeSVG value={registrationUrl} size={104} level="M" includeMargin />
              </div>
              <div className="min-w-0 flex-1 space-y-2 text-center sm:text-left">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Registration link
                </p>
                <p className="text-sm font-mono break-all text-foreground"
                   data-testid="registration-url">{registrationUrl}</p>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                  <ShareButton
                    getText={() => registrationInvite({
                      eventTitle: form.eventTitle || "This event",
                      url: registrationUrl,
                      message: form.registration_message,
                    })}
                    label="Share to WhatsApp"
                    testId="registration-share" />
                  <Button size="sm" variant="outline" className="h-10 gap-1.5" onClick={copyLink}
                          data-testid="registration-copy">
                    <Copy className="h-3.5 w-3.5" />Copy link
                  </Button>
                </div>
              </div>
            </div>
          )}

          {canEdit && (
            <div className="grid sm:grid-cols-2 gap-4 pt-1">
              <div className="space-y-1.5">
                <Label htmlFor="cap">Capacity (people, blank for no limit)</Label>
                <Input id="cap" type="number" min="1" className="h-11"
                       value={form.registration_capacity}
                       onChange={(e) => setForm({ ...form, registration_capacity: e.target.value })}
                       onBlur={() => save()}
                       data-testid="registration-capacity" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="msg">Message shown on the form</Label>
                <Textarea id="msg" rows={2} value={form.registration_message}
                          onChange={(e) => setForm({ ...form, registration_message: e.target.value })}
                          onBlur={() => save()}
                          data-testid="registration-message" />
              </div>
              <div className="flex items-center justify-between gap-3 sm:col-span-2 rounded-lg border p-3">
                <div>
                  <Label htmlFor="allow-party">Allow one ticket for several people</Label>
                  <p className="text-xs text-muted-foreground">
                    Off: every person registers themselves (recommended). On: the form asks "How many people".
                  </p>
                </div>
                <Switch id="allow-party" checked={form.registration_allow_party}
                        onCheckedChange={(v) => save({ registration_allow_party: v })}
                        disabled={saving}
                        data-testid="registration-allow-party" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {stats && (
        <div className="grid grid-cols-3 gap-2 text-center" data-testid="attendee-stats">
          {[
            ["Registered", stats.registered_people],
            ["Checked in", stats.checked_in_people],
            ["Not yet", stats.no_show_people ?? Math.max(0, (stats.registered_people || 0) - (stats.checked_in_people || 0))],
          ].map(([label, value]) => (
            <Card key={label}>
              <CardContent className="p-3">
                <p className="text-2xl font-bold text-foreground">{value ?? 0}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dietary is a safety question, so notes are shown in full with names. */}
      {dietary && (Object.keys(dietary.counts || {}).length > 0 || dietary.notes?.length > 0) && (
        <Card data-testid="dietary-rollup">
          <CardContent className="p-5 space-y-3">
            <p className="font-semibold text-foreground flex items-center gap-1.5">
              <Leaf className="h-4 w-4" aria-hidden="true" />Dietary requirements
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(dietary.counts || {}).map(([tag, count]) => (
                <Badge key={tag} variant="secondary">{tag}: {count}</Badge>
              ))}
            </div>
            {dietary.notes?.length > 0 && (
              <div className="space-y-1.5 pt-1">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Allergies and notes
                </p>
                {dietary.notes.map((n, i) => (
                  <p key={i} className="text-sm bg-amber-500/10 text-amber-700 rounded-md p-2">
                    <strong>{n.name}:</strong> {n.note}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="font-semibold text-foreground flex items-center gap-1.5">
            <Users className="h-4 w-4" aria-hidden="true" />
            {attendees.length} registration{attendees.length === 1 ? "" : "s"}
          </p>
          <div className="flex items-center gap-2">
            {attendees.length > 0 && (
              <>
                <Button size="sm" variant="outline" className="h-10 gap-1.5"
                        onClick={() => downloadList("csv")} data-testid="attendees-export-csv">
                  <Download className="h-3.5 w-3.5" aria-hidden="true" />CSV
                </Button>
                <Button size="sm" variant="outline" className="h-10 gap-1.5"
                        onClick={() => downloadList("pdf")} data-testid="attendees-export-pdf">
                  <FileText className="h-3.5 w-3.5" aria-hidden="true" />PDF
                </Button>
              </>
            )}
            <div className="relative w-40 sm:w-48">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="h-10 pl-9" placeholder="Search" aria-label="Search registrations" value={query}
                     onChange={(e) => setQuery(e.target.value)}
                     data-testid="attendee-search" />
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center space-y-2" data-testid="attendees-empty">
              <Users className="h-8 w-8 mx-auto text-muted-foreground" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">
                {attendees.length === 0
                  ? form.registration_open
                    ? "Nobody has registered yet. Share the link above."
                    : "Open registration to start taking sign-ups."
                  : "No registrations match that search."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((a) => (
              <Card key={a.attendee_id} data-testid={`attendee-${a.attendee_id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {a.name}
                      {a.party_size > 1 && (
                        <span className="text-muted-foreground font-normal">
                          {" "}· {a.party_size} people
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                    {a.dietary?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {a.dietary.join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.checked_in && (
                      <Badge className="bg-emerald-500/10 text-emerald-600 text-xs">In</Badge>
                    )}
                    <span className="font-mono text-xs text-muted-foreground">{a.code}</span>
                    {canEdit && (
                      <Button variant="ghost" size="sm" className="h-9 w-9 p-0"
                              aria-label={`Remove ${a.name}`}
                              onClick={() => removeAttendee(a)}
                              data-testid={`attendee-remove-${a.attendee_id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
